/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AnalyticsSignalDefinitionRequest,
  AnalyticsSignalDefinitionSecurity,
} from "../models/operations/analyticssignaldefinition.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AnalyticsSignalDefinitionQueryData,
  buildAnalyticsSignalDefinitionQuery,
  prefetchAnalyticsSignalDefinition,
  queryKeyAnalyticsSignalDefinition,
} from "./analyticsSignalDefinition.core.js";
export {
  type AnalyticsSignalDefinitionQueryData,
  buildAnalyticsSignalDefinitionQuery,
  prefetchAnalyticsSignalDefinition,
  queryKeyAnalyticsSignalDefinition,
};

export type AnalyticsSignalDefinitionQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * signalDefinition analytics
 *
 * @remarks
 * Inspect the immutable semantic definition used by a signal reading. Requires signals_intelligence and project:read.
 */
export function useAnalyticsSignalDefinition(
  request: AnalyticsSignalDefinitionRequest,
  security?: AnalyticsSignalDefinitionSecurity | undefined,
  options?: QueryHookOptions<
    AnalyticsSignalDefinitionQueryData,
    AnalyticsSignalDefinitionQueryError
  >,
): UseQueryResult<
  AnalyticsSignalDefinitionQueryData,
  AnalyticsSignalDefinitionQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildAnalyticsSignalDefinitionQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * signalDefinition analytics
 *
 * @remarks
 * Inspect the immutable semantic definition used by a signal reading. Requires signals_intelligence and project:read.
 */
export function useAnalyticsSignalDefinitionSuspense(
  request: AnalyticsSignalDefinitionRequest,
  security?: AnalyticsSignalDefinitionSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AnalyticsSignalDefinitionQueryData,
    AnalyticsSignalDefinitionQueryError
  >,
): UseSuspenseQueryResult<
  AnalyticsSignalDefinitionQueryData,
  AnalyticsSignalDefinitionQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAnalyticsSignalDefinitionQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAnalyticsSignalDefinitionData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      sensorId: string;
      definitionHash: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: AnalyticsSignalDefinitionQueryData,
): AnalyticsSignalDefinitionQueryData | undefined {
  const key = queryKeyAnalyticsSignalDefinition(...queryKeyBase);

  return client.setQueryData<AnalyticsSignalDefinitionQueryData>(key, data);
}

export function invalidateAnalyticsSignalDefinition(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      sensorId: string;
      definitionHash: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "analytics",
      "signalDefinition",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllAnalyticsSignalDefinition(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "analytics", "signalDefinition"],
  });
}
